// ============================================================================
// COMPONENTE: ESCOLAS GRID - EXIBIÇÃO ALEATÓRIA PARA HOME
// ============================================================================

import React, { useState, useEffect } from "react";
import { MapPin, ExternalLink, Loader2, School } from "lucide-react";

// ── Tipos ────────────────────────────────────────────────────────────────────

interface Escola {
  id: string;
  schoolName: string;
  city: string;
  state: string;
  address: string;
  logoUrl?: string;
  website?: string;
}

// ── Utilitários ───────────────────────────────────────────────────────────────

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// ── Componente ────────────────────────────────────────────────────────────────

const EscolasGrid: React.FC<{ maxEscolas?: number }> = ({ maxEscolas = 6 }) => {
  const [escolas, setEscolas] = useState<Escola[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const carregarEscolasAleatorias = async () => {
      setLoading(true);
      setError(null);
      try {
        const API_BASE =
          (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:4000/api/v1';

        const res = await fetch(`${API_BASE}/affiliates/public?isSchool=true&limit=2000`);
        if (!res.ok) throw new Error('Falha ao carregar escolas');

        const data = await res.json();
        const listaCompleta: Escola[] = (data.affiliates ?? []).map((a: any) => ({
          id: a.id || a._id || Math.random().toString(),
          schoolName: a.schoolName || a.name || 'Escola sem nome',
          city: a.city || '',
          state: a.state || '',
          address: a.address || '',
          logoUrl: a.logoUrl || a.logo || '',
          website: a.website || '',
        }));

        setEscolas(shuffleArray(listaCompleta).slice(0, maxEscolas));
      } catch (err) {
        console.error('Erro ao carregar escolas:', err);
        setError('Não foi possível carregar as escolas.');
      } finally {
        setLoading(false);
      }
    };
    carregarEscolasAleatorias();
  }, [maxEscolas]);

  // ── Estados de loading / erro / vazio ────────────────────────────────────

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-12 h-12 text-amber-600 animate-spin mb-4" />
        <p className="text-gray-600">Carregando escolas...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
        <School className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <p className="text-red-700 font-semibold">{error}</p>
      </div>
    );
  }

  if (escolas.length === 0) {
    return (
      <div className="text-center py-16">
        <School className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500">Nenhuma escola disponível no momento.</p>
      </div>
    );
  }

  // ── Grid ─────────────────────────────────────────────────────────────────

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
      {escolas.map((escola, index) => (
        <div
          key={escola.id}
          className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-amber-100 hover:border-amber-300 hover:-translate-y-1"
          style={{
            animationDelay: `${index * 100}ms`,
            animation: 'fadeInUp 0.6s ease-out forwards',
          }}
        >
          {/* ── Header com logo ── */}
          <div className="h-24 bg-gradient-to-br from-amber-50 to-orange-50 relative overflow-hidden">
            {/* Glows decorativos */}
            <div className="absolute inset-0 opacity-20 pointer-events-none">
              <div className="absolute top-0 right-0 w-20 h-20 bg-amber-400 rounded-full blur-2xl" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-orange-400 rounded-full blur-3xl" />
            </div>

            {/* Logo ou ícone padrão — centralizado no header */}
            <div className="absolute inset-0 flex items-center justify-center">
              {escola.logoUrl ? (
                <img
                  src={escola.logoUrl}
                  alt={escola.schoolName}
                  className="w-16 h-16 rounded-xl object-cover border-4 border-white shadow-lg bg-white"
                  onError={(e) => {
                    const el = e.currentTarget as HTMLImageElement;
                    el.src = '/icons/icon14.png';
                    el.className = 'w-16 h-16 rounded-xl object-contain border-4 border-white shadow-lg bg-white p-1';
                  }}
                />
              ) : (
                <img
                  src="/icons/icon14.png"
                  alt="Escola"
                  className="w-16 h-16 rounded-xl object-contain border-4 border-white shadow-lg bg-white p-1"
                />
              )}
            </div>
          </div>

          {/* ── Conteúdo ── */}
          <div className="pt-5 pb-5 px-5">

            {/* Nome */}
            <h3 className="text-lg font-black text-gray-900 mb-2 text-center leading-tight px-2">
              {escola.schoolName}
            </h3>

            {/* Localização */}
            {(escola.city || escola.state) && (
              <div className="flex items-center justify-center gap-1.5 text-gray-600 mb-3">
                <MapPin className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-sm">
                  {escola.city}{escola.state ? `, ${escola.state}` : ''}
                </span>
              </div>
            )}

            {/* Estrelas */}
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-4 h-4 text-amber-500 fill-amber-500" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>

            {/* Badge credenciada */}
            <div className="flex items-center justify-center gap-2 bg-emerald-50 rounded-full px-3 py-1.5 border border-emerald-200">
              <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              <span className="text-xs font-bold text-emerald-700">Credenciada pela Federação</span>
            </div>

            {/* Botão website (opcional) */}
            {escola.website && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <a
                  href={escola.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-xl hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 group"
                >
                  <span className="text-sm">Visitar Site</span>
                  <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </a>
              </div>
            )}
          </div>
        </div>
      ))}

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default EscolasGrid;